/**
 * External Mapping admin — Yemeksepeti/Getir/Trendyol ürün+modifier eşleme.
 */
import { Card, Stack, Tabs, Table, Badge, Loader, Center, Button, Group, ActionIcon } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useResource, useRemove } from "../../../shared/api";
import { PageHeader, EmptyState, ResourceForm, confirmDelete, type FormField } from "../../../shared/ui";

const PROVIDER_COLOR: Record<string, string> = {
  GETIR: "violet",
  YEMEKSEPETI: "red",
  TRENDYOL: "orange",
  UBEREATS: "dark",
};

const PRODUCT_FIELDS: FormField[] = [
  { name: "internal_product", label: "İç Ürün ID (UUID)", required: true },
  { name: "provider", label: "Provider (GETIR/YEMEKSEPETI/TRENDYOL)", required: true, defaultValue: "YEMEKSEPETI" },
  { name: "external_product_id", label: "Dış Ürün ID", required: true },
];

const MODIFIER_FIELDS: FormField[] = [
  { name: "internal_modifier", label: "İç Modifier ID (UUID)", required: true },
  { name: "provider", label: "Provider", required: true, defaultValue: "YEMEKSEPETI" },
  { name: "external_modifier_id", label: "Dış Modifier ID", required: true },
  { name: "external_group_id", label: "Dış Group ID (Getir option_group)" },
];

export function ExternalMappingPage() {
  return (
    <Stack gap="md">
      <PageHeader
        title="Delivery Platform Eşlemeleri"
        subtitle="Getir / Yemeksepeti / Trendyol ürün ve modifier ID eşlemesi"
      />
      <Tabs defaultValue="products">
        <Tabs.List>
          <Tabs.Tab value="products">Ürün Eşlemeleri</Tabs.Tab>
          <Tabs.Tab value="modifiers">Modifier Eşlemeleri</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="products" pt="md">
          <MappingList resource="external-product-mappings" fields={PRODUCT_FIELDS} nameField="internal_product_name" idField="external_product_id" />
        </Tabs.Panel>
        <Tabs.Panel value="modifiers" pt="md">
          <MappingList resource="external-modifier-mappings" fields={MODIFIER_FIELDS} nameField="internal_modifier_name" idField="external_modifier_id" />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

function MappingList({ resource, fields, nameField, idField }: { resource: string; fields: FormField[]; nameField: string; idField: string }) {
  const { data, isLoading } = useResource<any>(resource);
  const remove = useRemove(resource);
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={200}><Loader /></Center>;

  async function handleDelete(id: string) {
    if (!(await confirmDelete("Eşleme silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>Yeni Eşleme</Button>
      </Group>
      {list.length === 0 ? <EmptyState title="Eşleme yok" description="Delivery platform entegrasyonu için ürün/modifier ID'lerini eşleyin." /> : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={560}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>İç Ürün/Modifier</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Dış ID</Table.Th>
                <Table.Th>Son Sync</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((m: any) => (
                <Table.Tr key={m.id}>
                  <Table.Td fw={500}>{m[nameField] || "-"}</Table.Td>
                  <Table.Td><Badge color={PROVIDER_COLOR[m.provider] || "gray"}>{m.provider}</Badge></Table.Td>
                  <Table.Td><code style={{ fontSize: 11 }}>{m[idField]}</code></Table.Td>
                  <Table.Td>{m.last_synced_at ? new Date(m.last_synced_at).toLocaleString("tr-TR") : "-"}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => { setEditing(m); open(); }}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(m.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource={resource}
        title={editing ? "Eşleme Düzenle" : "Yeni Eşleme"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
